'use client';

import { Button, Modal } from '@common/components';
import { cn, isEdge } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useTransition } from 'react';

import { UnauthQueryOptions } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { DONATION_ROUTES, ROUTES, STORAGE_KEY } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useVerificationCheck } from '@/hooks/useVerificationCheck';
import { useAuthStore } from '@/stores/AuthStore';
import { useDebugStore } from '@/stores/DebugStore';
import { gtmDatasets } from '@/utils/analytics/gtm';

import { DonationDebugPanel } from '../DebugPanel';
import { DonationReservationCompleteModal } from '../DonationReservationCompleteModal';
import { DonationReservationModal } from '../DonationReservationModal';
import { useIsDonationHistory } from './_hooks/useIsDonationHistory';
import { AgreementSection } from './AgreementSection';
import { PrefillForm } from './PrefillForm';

interface DonationButtonProps {
  institutionId: number;
  donationBusinessId?: number;
  institutionName: string;
  disabled?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

export const DonationButton = ({
  institutionId,
  donationBusinessId,
  institutionName,
  disabled,
  children,
  style,
  className,
}: DonationButtonProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const isLogin = useAuthStore((state) => !!state.isLogin);
  const { isDonationHistory } = useIsDonationHistory();
  const isEdgeBrowser = isEdge();
  const [, startTransition] = useTransition();
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isReservationCompleteModalOpen, setIsReservationCompleteModalOpen] =
    useState(false);

  const router = useCustomRouter();

  //* 본인인증 체크 훅
  const { checkVerification, isLoading: isVerificationLoading } =
    useVerificationCheck({
      enabled: isLogin,
    });
  const pathname = usePathname();

  type DonationStep = 'closed' | 'product-check' | 'agreement' | 'prefill';

  const [step, setStep] = useState<DonationStep>('closed');
  const [isEdgeModal, setIsEdgeModal] = useState(false);
  const skipReservation = useDebugStore((s) => s.skipReservation);

  const { data: donationReservation } = useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/control-managements'](),
    select: (data) => (skipReservation ? null : data.context),
    staleTime: 0,
    gcTime: 0,
  });

  const handleStart = () => {
    if (isEdgeBrowser) {
      setIsEdgeModal(true);

      return;
    }

    //* 미로그인 시 로그인 페이지로 이동 (현재 페이지 URL을 redirect 파라미터로 전달)
    if (!isLogin) {
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: pathname ?? '' },
        }),
      );

      return;
    }

    //* 본인인증 정보 없을 경우
    if (checkVerification()) {
      return;
    }

    //* 예약된 기부 상태인 경우
    if (donationReservation) {
      setStep('closed');
      setIsReservationModalOpen(true);

      return;
    }

    //* 모든 검증 통과 시 답례품 선택 단계로 진행
    setStep('product-check');
  };

  const handleDonationWithoutGift = () => {
    //* 아직 동의하지 않은 경우
    const isAgreement = sessionStorage.getItem(STORAGE_KEY.DONATION_AGREEMENT);
    if (!isDonationHistory && !isAgreement) {
      setStep('agreement');

      return;
    }

    setStep('prefill');
  };

  const handleDonationWithoutGiftAfterAgreement = () => {
    sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, 'true');

    router.push(
      DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: {
          donationBusinessId,
          institutionId,
          institutionName,
          from: pathname ?? undefined,
        },
      }),
    );
  };

  return (
    <>
      <Button
        loading={isVerificationLoading}
        onClick={handleStart}
        className={cn('w-full', className)}
        disabled={disabled}
        style={style}
      >
        {children || '기부하기'}
      </Button>

      {/* 약관 동의 모달 (최초 기부 시에만 표시) */}
      <ResponsiveDialog
        opened={step === 'agreement'}
        onClose={() => setStep('closed')}
        drawerType="bottom"
        title="고향사랑기부 서비스를 이용하기 위해 꼭 필요한 동의만 추렸어요"
        modalHeader={{
          title:
            '고향사랑기부 서비스를 이용하기 위해 꼭 필요한 동의만 추렸어요',
        }}
      >
        <AgreementSection onNext={handleDonationWithoutGiftAfterAgreement} />
      </ResponsiveDialog>

      {/* 답례품 선택 여부 확인 모달 */}
      <ResponsiveDialog
        opened={step === 'product-check'}
        onClose={() => setStep('closed')}
        drawerType="bottom"
        modalHeader={{
          title: '기부 금액에 맞는\n답례품을 선택해 주세요',
          description:
            '고향사랑기부금의 30% 내에서 답례품을 무료로 받아보세요!',
        }}
      >
        {!isDesktopViewport ? (
          <>
            <h3 className="mb-26 break-keep pb-12 text-center font-700 text-Gray-900 typo-body1">
              기부 금액에 맞는
              <br />
              답례품을 선택해 주세요
            </h3>
            <p className="mb-32 text-center font-400 text-Gray-800 typo-body3">
              고향사랑기부금의 30% 내에서
              <br />
              답례품을 무료로 받아보세요!
            </p>
          </>
        ) : null}
        <div className="flex flex-col gap-8">
          <Button dimensions={isDesktopViewport ? 'H56' : 'H48'} asChild>
            <Link
              onClick={() => setStep('closed')}
              href={DONATION_ROUTES.HOMETOWNS_DETAIL.withSearchParams({
                dynamicPath: {
                  id: institutionId,
                },
                searchParams: {
                  tab: 'gifts',
                },
              })}
              {...gtmDatasets.startGift({
                local_name: institutionName,
              })}
            >
              답례품 선택 후 기부하기
            </Link>
          </Button>
          <Button
            dimensions={isDesktopViewport ? 'H56' : 'H48'}
            theme="sub-text"
            onClick={handleDonationWithoutGift}
            {...gtmDatasets.startDonation({
              local_name: institutionName,
            })}
          >
            답례품 없이 기부하기
          </Button>
        </div>
      </ResponsiveDialog>

      <ResponsiveDialog
        opened={step === 'prefill'}
        onClose={() => setStep('closed')}
        drawerType="bottom"
        modalHeader={{
          title: '기부금 선택',
        }}
        title="기부금 선택"
      >
        <PrefillForm
          institutionId={institutionId}
          defaultDonationBusinessId={donationBusinessId}
          onSubmit={(data) => {
            setStep('closed');
            sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, 'true');
            router.push(
              DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
                searchParams: {
                  donationBusinessId,
                  institutionId,
                  institutionName,
                  initialAmount: data.amount,
                  initialPurpose: data.purpose,
                  from: pathname ?? undefined,
                },
              }),
            );
          }}
        />
      </ResponsiveDialog>

      {/* Edge 브라우저 제한 안내 모달 */}
      <Modal opened={isEdgeModal} onClose={() => setIsEdgeModal(false)}>
        <div className="flex flex-col gap-16 text-center">
          <h2 className="font-700 text-Gray-900 typo-body2">
            웰로의 고향사랑기부제는 엣지 브라우저에서 지원되지 않습니다.
          </h2>
          <p className="font-400 text-Gray-700 typo-body3">
            원활한 이용을 위해 Chrome, Safari 등의 브라우저를 이용해 주세요.
          </p>
          <Button onClick={() => setIsEdgeModal(false)}>확인</Button>
        </div>
      </Modal>

      <DonationReservationModal
        opened={isReservationModalOpen}
        businessId={donationBusinessId}
        onClose={() => setIsReservationModalOpen(false)}
        onSuccess={() => {
          setIsReservationModalOpen(false);

          startTransition(() => {
            setIsReservationCompleteModalOpen(true);
          });
        }}
        description={donationReservation?.contents}
      />

      <DonationReservationCompleteModal
        opened={isReservationCompleteModalOpen}
        onClose={() => setIsReservationCompleteModalOpen(false)}
      />
      <DonationDebugPanel />
    </>
  );
};
